import React, { useState, useMemo, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { withTranslation } from "react-i18next";
import {
  Box,
  Button,
  IconButton,
  Typography,
  useMediaQuery,
} from "@mui/material";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import EditIcon from "@mui/icons-material/Edit";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { ChartIcon } from "Components/Common/Styled/ChartIcon";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import DescriptionIcon from "@mui/icons-material/Description";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import moment from "moment";
import PropTypes from "prop-types";

import BreadCrumb from "Components/Common/BreadCrumb";
import BaseTable from "Components/Common/BaseTable";
import FilterApplicationRequests from "Components/Common/FilterApplicationRequest";
import DocumentPreviewModal from "Components/Common/DocumentPreviewModal";
import ApplicationStatusHistoryChart from "Components/Charts/ApplicationStatusHistoryChart";
import Modal from "Components/Common/Modal";
import Tooltip from "Components/Common/Tooltip";
import PaymentRequest from "Components/PaymentRequest";
import OtherApplicationsEditModal from "Components/OtherApplicationEditModal";
import {
  applicationStatusTypes,
  applicationTypes,
  CONSUMER,
  estimateIssued,
} from "common/constant";
import DownloadUploadActionButton from "pages/Consumer/ApplicationRequestList/DownloadUploadActionButton";
import DemandEstimatePaymentModal from "pages/Consumer/ApplicationRequestList/DemandEstimatePaymentModal";
import BankInformationFormModal from "pages/Consumer/BankInformationFormModal";
import PartialDocumentUploadModal from "pages/Consumer/ApplicationRequestList/PartialDocumentUploadModal";
import { getOptionLabel } from "common/enums";
import OtherApplicationViewModal from "Components/OtherApplicationViewModal";

import { getImageUrl } from "utils";
import { convertUTCDateToLocalDate } from "utils/convertUTCDateToLocalDate";
import { formatUtilityAddress } from "utils/formatUtilityAddress";

import {
  APPROVED_APPLICATION_DOCUMENT,
  OTHER_APPLICATION_DETAILS,
} from "tagTypes";

import {
  usePostRefundCalculationPaymentMutation,
  useReOpenExpiredApplicationMutation,
} from "apis/DemandEstimation";
import { useSelector } from "react-redux";
import ConfirmationDialog from "Components/Common/ConfirmationDialog";

const RECORDS_PER_PAGE = 10;

const editableStatus = ["saved", "commented", "submitted"];
const pendingDocumentNonUploadableStatus = ["saved", "submitted", "rejected"];

const OtherApplicationRequestList = ({ t }) => {
  const [getPaymentUrlForRefundCalculation] =
    usePostRefundCalculationPaymentMutation();
  const [reOpenApplication, { isLoading }] =
    useReOpenExpiredApplicationMutation();

  const [query, setQuery] = useState({});

  const { roles } = useSelector((state) => state.Login);
  const isConsumer = useMemo(() => roles.includes(CONSUMER), [roles]);

  const lg = useMediaQuery("(min-width: 768px) and (max-width: 1399px)");
  const md = useMediaQuery("(min-width: 576px) and (max-width: 767px)");
  const sm = useMediaQuery("(max-width: 575px)");

  const [editApplicationQuery, setEditApplicationQuery] = useState("");
  const [applicationTypeId, setApplicationTypeId] = useState("");
  const [applicationTypeName, setApplicationTypeName] = useState("");
  const [applicationIdForChartModal, setApplicationIdForChartModal] =
    useState("");
  const [
    isDemandEstimatePaymentModalOpen,
    setIsDemandEstimatePaymentModalOpen,
  ] = useState(false);
  const [reOpenExpiredApplication, setReOpenExpiredApplication] = useState("");

  const [
    isPreviewRefundCalculationBreakupModalOpen,
    setIsPreviewRefundCalculationBreakupModalOpen,
  ] = useState(false);
  const [isTaxInvoiceModalOpen, setIsTaxInvoiceModalOpen] = useState(false);
  const [isDemandEstimateModalOpen, setIsDemandEstimateModalOpen] =
    useState(false);

  const [applicationId, setApplicationId] = useState("");
  const [bankInformationModalProps, setBankInformationModalProps] =
    useState(null);
  const [applicationIdForRefundPayment, setApplicationIdForRefundPayment] =
    useState("");
  const [finalRefundAmountToConsumer, setFinalRefundAmountToConsumer] =
    useState("");
  const [isResetPageRegistered, setIsResetPageRegistered] = useState(false);
  const [documentToPreview, setDocumentToPreview] = useState();

  const resetPageRef = useRef();

  const [searchParams, setSearchParams] = useSearchParams();
  const viewApplicationId =
    searchParams.get("applicationId") || searchParams.get("groupId");
  const applicationRequestId = searchParams.get("applicationRequestId");

  const handleResetPage = useCallback(resetPageRef.current, [
    isResetPageRegistered,
  ]);

  const columns = [
    {
      title: "Application Number",
      field: "applicationNumber",
    },
    {
      title: "Application Type",
      field: "applicationType.name",
    },
    {
      title: "Utility",
      renderCell: ({ utilities }) =>
        utilities
          .map((val) => getOptionLabel("utilityOptions", val))
          .join(", "),
    },
    {
      title: "Name",
      field: "name",
    },
    {
      title: "Utility Address",
      renderCell: ({ utilityAddress }) => {
        const address = formatUtilityAddress(utilityAddress);

        if (address) {
          return (
            <>
              <p className="m-0">{address}, Gandhinagar-382050</p>
            </>
          );
        }

        return "--";
      },
      columnProps: { style: { width: "350px", minWidth: "350px" } },
    },
    {
      title: "Date",
      renderCell: ({ submissionDate }) =>
        moment(submissionDate).isValid()
          ? convertUTCDateToLocalDate(submissionDate).format("DD-MM-YYYY")
          : "N/A",
    },
    {
      title: "Status",
      renderCell: (item) => {
        return (
          <>
            {item.applicationStatus.name}
            {item?.applicationStatus?.name?.toLowerCase() === "approved" &&
            !item?.approvedApplicationDocumentURL ? (
              <Tooltip
                title={t(
                  "Your application request has been approved. Please download the application form, sign it, and then upload it again"
                )}
              >
                <InfoOutlinedIcon
                  className="ms-1 cursor-pointer"
                  color="primary"
                  fontSize="small"
                />
              </Tooltip>
            ) : item?.applicationStatus?.name?.toLowerCase() === "rejected" &&
              !item?.isTechnicalFeasibility ? (
              <Tooltip
                title={t(
                  "Location is not technically feasible for this application."
                )}
              >
                <InfoOutlinedIcon
                  className="ms-1 cursor-pointer"
                  color="primary"
                  fontSize="small"
                />
              </Tooltip>
            ) : (
              ""
            )}
          </>
        );
      },
    },
    {
      title: "Actions",
      // columnProps:{style={textAlign:"center"}}
      renderCell: (item) => {
        const {
          applicationStatus,
          groupId,
          id,
          demandEstimateId,
          refundCalculationUrl,
          demandEstimateUrl,
          applicationType,
          finalRefundAmount,
          isRefundCalculatedByFinance,
          taxInvoiceUrl,
          isTaxInvoiceApproved,
          utilityConsumerId,
          isDemandEstimateDocumentApproved,
          demandEstimateGrandTotal,
          isPartialDocumentsUploaded,
          isAllPendingDocumentsApproved,
          settlementOfConnectionCharge,
          settlementOfConnectionUrl,
          isSettlementConnectionChargeDocumentApproved,
          bankDetailId,
        } = item;
        const isEditDisable = !editableStatus.includes(
          applicationStatus.name.toLowerCase()
        );

        const applicationStatusName = applicationStatus.name.toLowerCase();
        const isUploadDocumentsIconVisible =
          !pendingDocumentNonUploadableStatus.includes(applicationStatusName) &&
          (!isPartialDocumentsUploaded || !isAllPendingDocumentsApproved);

        const isEstimationIssued = applicationStatusName === estimateIssued;

        const isMeterRemovedStatus =
          applicationStatus.id === applicationStatusTypes["Disconnected"];
        return (
          <Box width="150px">
            {/* View */}
            <IconButton
              onClick={() => {
                setApplicationTypeId(applicationType.id);
                setApplicationTypeName(applicationType.name);
                if (applicationStatusName === "saved") {
                  searchParams.set("groupId", groupId);
                } else {
                  searchParams.set("applicationId", id);
                }
                setSearchParams(searchParams);
              }}
            >
              <Tooltip title={t("View Application Form")}>
                <RemoveRedEyeIcon
                  fontSize="small"
                  color="primary"
                  className="cursor-pointer"
                />
              </Tooltip>
            </IconButton>
            {/* Edit */}
            {!isEstimationIssued && (
              <IconButton
                disabled={isEditDisable}
                onClick={() => {
                  setApplicationTypeId(applicationType.id);
                  setEditApplicationQuery(
                    applicationStatusName === "saved"
                      ? { groupId }
                      : { applicationId: id }
                  );
                }}
              >
                <Tooltip title={t("Edit Application Form")}>
                  <EditIcon
                    fontSize="small"
                    className="cursor-pointer"
                    color={isEditDisable ? "disabled" : "primary"}
                  />
                </Tooltip>
              </IconButton>
            )}
            {/* Download and upload form */}
            <DownloadUploadActionButton item={item} />
            {/* Pay now */}
            {isEstimationIssued &&
              (demandEstimateGrandTotal > 0 ||
                (settlementOfConnectionCharge > 0 &&
                  isSettlementConnectionChargeDocumentApproved)) && (
                <IconButton
                  onClick={() => {
                    setDocumentToPreview({
                      demandEstimateId: demandEstimateId,
                      url: getImageUrl(
                        settlementOfConnectionCharge > 0
                          ? settlementOfConnectionUrl
                          : demandEstimateUrl
                      ),
                      documentTypeName:
                        settlementOfConnectionCharge > 0
                          ? "Settlement Charge"
                          : "Demand Estimate",
                      documentType: "pdf",
                      documentName: "demandEstimate",
                    });
                    setApplicationId(id);
                    setIsDemandEstimateModalOpen(true);
                  }}
                >
                  <Tooltip title={t("Pay Now")}>
                    <CreditCardIcon
                      fontSize="medium"
                      className="cursor-pointer"
                      color="primary"
                      sx={{ width: "23px" }}
                    />
                  </Tooltip>
                </IconButton>
              )}
            {((demandEstimateGrandTotal < 0 &&
              [
                applicationTypes["Demand Reduction"],
                applicationTypes["Demand Extension"],
              ].includes(applicationType.id) &&
              isDemandEstimateDocumentApproved &&
              !!demandEstimateId) ||
              (!!isMeterRemovedStatus &&
                applicationType.id ===
                  applicationTypes["Removal of Connection"] &&
                !!finalRefundAmount &&
                finalRefundAmount > 0 &&
                isRefundCalculatedByFinance) ||
              (settlementOfConnectionCharge < 0 &&
                isSettlementConnectionChargeDocumentApproved &&
                !bankDetailId)) && (
              <Tooltip title={t("Bank Information")}>
                <IconButton
                  onClick={() => {
                    setBankInformationModalProps({
                      utilityConsumerId,
                      applicationId: id,
                    });
                  }}
                  size="small"
                >
                  <AccountBalanceIcon color="primary" />
                </IconButton>
              </Tooltip>
            )}
            {isRefundCalculatedByFinance &&
              finalRefundAmount < 0 &&
              isMeterRemovedStatus && (
                <Tooltip title="Pay Now">
                  <IconButton
                    onClick={() => {
                      setApplicationIdForRefundPayment(id);
                      setFinalRefundAmountToConsumer(finalRefundAmount);
                    }}
                    size="small"
                  >
                    <CreditCardIcon color="primary" />
                  </IconButton>
                </Tooltip>
              )}

            {!!taxInvoiceUrl && isTaxInvoiceApproved && (
              <IconButton
                onClick={() => {
                  setDocumentToPreview({
                    url: getImageUrl(taxInvoiceUrl),
                    documentTypeName: "Tax Invoice",
                    documentType: "pdf",
                    documentName: "Tax Invoice",
                    handleClose: () => setDocumentToPreview(null),
                  });
                  setIsTaxInvoiceModalOpen(true);
                }}
              >
                <Tooltip title={t("Tax Invoice")}>
                  <DescriptionIcon color="primary" />
                </Tooltip>
              </IconButton>
            )}
            {!!refundCalculationUrl && (
              <Tooltip title="Refund Calculations Document">
                <IconButton
                  onClick={() => {
                    setDocumentToPreview({
                      url: getImageUrl(refundCalculationUrl),
                      documentTypeName: "Refund Calculations Breakup Document",
                      documentType: "pdf",
                      documentName: "Refund Calculations Breakup",
                    });
                    setIsPreviewRefundCalculationBreakupModalOpen(true);
                  }}
                  size="small"
                >
                  <InsertDriveFileIcon color="primary" />
                </IconButton>
              </Tooltip>
            )}
            {/* Upload Documents */}
            {isUploadDocumentsIconVisible && (
              <IconButton
                onClick={() => {
                  searchParams.set("applicationRequestId", id);
                  setSearchParams(searchParams);
                }}
              >
                <Tooltip title={t("Upload Documents")}>
                  <UploadFileIcon
                    fontSize="small"
                    color="primary"
                    className="cursor-pointer"
                  />
                </Tooltip>
              </IconButton>
            )}
            {applicationStatus?.name?.toLowerCase() === "expired" &&
              !!isConsumer && (
                <IconButton
                  onClick={() => {
                    setReOpenExpiredApplication(id);
                  }}
                >
                  <Tooltip title={t("Submit Reopen Request")}>
                    <EventRepeatIcon
                      fontSize="small"
                      color="primary"
                      className="cursor-pointer"
                    />
                  </Tooltip>
                </IconButton>
              )}
            <IconButton onClick={() => setApplicationIdForChartModal(id)}>
              <Tooltip title={t("Application Status History Chart")}>
                <ChartIcon />
              </Tooltip>
            </IconButton>
          </Box>
        );
      },
    },
  ];

  const serverSidePaginationConfig = useMemo(
    () => ({
      endpoint: "/other-utility-application",
      tags: [OTHER_APPLICATION_DETAILS, APPROVED_APPLICATION_DOCUMENT],
      query,
    }),
    [query]
  );
  const submitHandlerForReOpenApplication = async () => {
    try {
      await reOpenApplication(reOpenExpiredApplication);
      setReOpenExpiredApplication("");
    } catch (err) {}
  };

  return (
    <>
      {!!(applicationTypeId && editApplicationQuery) && (
        <OtherApplicationsEditModal
          applicationTypeId={applicationTypeId}
          editApplicationQuery={editApplicationQuery}
          handleClose={() => setEditApplicationQuery("")}
        />
      )}

      {!!reOpenExpiredApplication && (
        <ConfirmationDialog
          message="Are you sure you want to sent reopen application request?"
          onCancel={() => setReOpenExpiredApplication("")}
          onConfirm={submitHandlerForReOpenApplication}
          isOpen={!!reOpenExpiredApplication}
          title="info"
          variant="info"
          isLoading={isLoading}
        />
      )}

      {!!applicationIdForRefundPayment && (
        <Modal
          isOpen
          handleClose={() => {
            setFinalRefundAmountToConsumer("");
            setApplicationIdForRefundPayment("");
          }}
          title="Refund Calculations Payment"
          maxWidth="sm"
        >
          <div className="mt-3">
            <Typography>
              You need to pay
              <b> RS. {Math.abs(finalRefundAmountToConsumer)}/- </b>
              to complete the application process.
            </Typography>
          </div>

          <div className="text-end mt-3">
            <PaymentRequest
              generateEncryption={async () =>
                await getPaymentUrlForRefundCalculation({
                  applicationId: applicationIdForRefundPayment,
                })
              }
              isPowerUtility={false}
            >
              <Button variant="contained">{t("Pay Now")}</Button>
            </PaymentRequest>
          </div>
        </Modal>
      )}

      {isDemandEstimatePaymentModalOpen && !!applicationId && (
        <DemandEstimatePaymentModal
          applicationId={applicationId}
          handleClose={() => setIsDemandEstimatePaymentModalOpen(false)}
          handleCloseForSubmit={() => {
            setApplicationId("");
            setIsDemandEstimateModalOpen(false);
          }}
        />
      )}

      {!!bankInformationModalProps && (
        <BankInformationFormModal
          bankInformationModalProps={bankInformationModalProps}
          handleClose={() => {
            setBankInformationModalProps(null);
          }}
        />
      )}

      {!!documentToPreview && isDemandEstimateModalOpen && (
        <DocumentPreviewModal
          isOpen
          handleClose={() => setDocumentToPreview(null)}
          actionButton={
            <Button
              onClick={() => setIsDemandEstimatePaymentModalOpen(true)}
              variant="contained"
            >
              {t("Pay Now")}
            </Button>
          }
          {...documentToPreview}
        />
      )}

      {!!documentToPreview && isTaxInvoiceModalOpen && (
        <DocumentPreviewModal
          isOpen
          handleClose={() => {
            setDocumentToPreview(null);
            setIsTaxInvoiceModalOpen(false);
          }}
          {...documentToPreview}
        />
      )}

      {isPreviewRefundCalculationBreakupModalOpen && !!documentToPreview && (
        <DocumentPreviewModal
          isOpen
          handleClose={() => {
            setIsPreviewRefundCalculationBreakupModalOpen(false);
            setDocumentToPreview(null);
          }}
          {...documentToPreview}
        />
      )}

      {!!applicationIdForChartModal && (
        <Modal
          isOpen
          handleClose={() => setApplicationIdForChartModal("")}
          title="Application Status History Chart"
          maxWidth="md"
        >
          <ApplicationStatusHistoryChart
            applicationId={applicationIdForChartModal}
          />
        </Modal>
      )}

      {!!viewApplicationId && (
        <OtherApplicationViewModal
          viewApplicationQuery={{
            [searchParams.has("groupId") ? "groupId" : "applicationId"]:
              viewApplicationId,
          }}
          applicationType={applicationTypeName}
          handleClose={() => {
            searchParams.delete(
              searchParams.has("groupId") ? "groupId" : "applicationId"
            );
            setSearchParams(searchParams);
          }}
        />
      )}

      {!!applicationRequestId && <PartialDocumentUploadModal />}

      <div>
        <BreadCrumb title={t("Application Request Details")} />
        <FilterApplicationRequests
          setQuery={setQuery}
          resetPage={handleResetPage}
        />

        <BaseTable
          columns={columns}
          pageSize={RECORDS_PER_PAGE}
          paginationType="serverSide"
          serverSidePaginationConfig={serverSidePaginationConfig}
          registerResetPage={(cb) => {
            resetPageRef.current = cb;
            setIsResetPageRegistered((prev) => !prev);
          }}
          maxHeight={
            sm
              ? "calc(100vh - 400px)"
              : md
              ? "calc(100vh - 360px)"
              : lg
              ? "calc(100vh - 320px)"
              : "calc(100vh - 270px)"
          }
        />
      </div>
    </>
  );
};

OtherApplicationRequestList.propTypes = {
  t: PropTypes.func,
};

export default withTranslation()(OtherApplicationRequestList);
